import { useCallback, useState } from 'react';
import { useBlocker } from '@tanstack/react-router';

export interface UnsavedChangesGuard {
  /** True while a leave attempt is pending the user's confirmation. */
  isPrompting: boolean;
  /** Proceed with the blocked navigation / pending action. */
  confirmLeave: () => void;
  /** Stay on the page; cancel the blocked navigation / pending action. */
  cancelLeave: () => void;
  /**
   * Wrap an in-app action that is NOT a router navigation (e.g. a "Back"
   * button that only flips component state). If there are unsaved changes the
   * action is deferred until the user confirms; otherwise it runs immediately.
   */
  guard: (action: () => void) => void;
}

/**
 * Warns the user before they navigate away while `hasUnsavedChanges` is true.
 *
 * Covers three exit paths:
 * - In-app router navigation (clicking another route) — held by the TanStack
 *   Router blocker and surfaced through `isPrompting` / `confirmLeave`.
 * - Browser unload (tab close / refresh / hard nav) — the native prompt, via
 *   the blocker's `enableBeforeUnload`.
 * - In-app actions that are not router navigations — wrap them with `guard()`.
 *
 * Render your own confirmation UI (e.g. `ConfirmDialog`) off `isPrompting`,
 * wiring its confirm/cancel to `confirmLeave` / `cancelLeave`.
 */
export function useUnsavedChangesGuard(
  hasUnsavedChanges: boolean,
): UnsavedChangesGuard {
  const blocker = useBlocker({
    shouldBlockFn: () => hasUnsavedChanges,
    enableBeforeUnload: () => hasUnsavedChanges,
    withResolver: true,
  });

  // A non-router action (e.g. back button) awaiting confirmation.
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

  const guard = useCallback(
    (action: () => void) => {
      if (hasUnsavedChanges) {
        // Store the action itself, not a state-updater that would call it.
        setPendingAction(() => action);
      } else {
        action();
      }
    },
    [hasUnsavedChanges],
  );

  const isPrompting = blocker.status === 'blocked' || pendingAction !== null;

  const confirmLeave = useCallback(() => {
    if (pendingAction) {
      const action = pendingAction;
      setPendingAction(null);
      action();
      return;
    }
    if (blocker.status === 'blocked') {
      blocker.proceed();
    }
  }, [pendingAction, blocker]);

  const cancelLeave = useCallback(() => {
    if (pendingAction) {
      setPendingAction(null);
      return;
    }
    if (blocker.status === 'blocked') {
      blocker.reset();
    }
  }, [pendingAction, blocker]);

  return { isPrompting, confirmLeave, cancelLeave, guard };
}
